// src/components/SeriesContent.js
// Shared presentational pieces for a CHALLENGE (series) detail view.
//
// Extracted from Workouts.js (2026-09-14) so the in-app member view and the
// public, signed-out challenge page render the same challenge identically.
// ONE definition on purpose: a stranger and a member are looking at the same
// thing, and if the two renderings drift it is the public one -- the surface
// aimed at people who don't know us yet -- that quietly rots, because it's the
// one nobody on the team sees daily. Same reasoning as CompletionHistoryList.
// See docs/decisions/adr-public-series-access.md section 4.
//
// Pure presentation: no data fetching, no completion writes, no auth. Every
// block returns null when its data is absent, so a challenge created before
// these fields existed renders exactly as it did then.

import React, { useState, useEffect, useRef } from 'react';
import FormattedText from './FormattedText';

// ══════════════════════════════════════════════════════════════════════════
// SERIES detail — rich content blocks + share control.
//
// All module scope on purpose: components defined inside Workouts would be a
// new type on every parent render, remounting them (collapsing open FAQ rows,
// dropping focus). Hover/open state is local useState, never DOM mutation.
// Every block returns null when its data is absent, so a challenge created
// before these fields existed renders exactly as it did then.
// See docs/decisions/adr-series-deep-link-and-sections.md.
// ══════════════════════════════════════════════════════════════════════════

// Shared glass card shell for the info blocks.
const SERIES_CARD_STYLE = {
    background: 'rgba(255,255,255,0.6)',
    backdropFilter: 'blur(20px) saturate(1.4)',
    WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
    border: '1px solid rgba(255,255,255,0.6)',
    borderRadius: 20,
    boxShadow: 'var(--shadow-sm)',
    padding: '22px 24px',
    marginBottom: 16,
};

const SERIES_HEADING_STYLE = {
    fontFamily: 'var(--mom-font-serif)',
    fontSize: 19,
    fontStyle: 'italic',
    color: 'var(--mom-charcoal)',
    marginBottom: 14,
};

// Share the open challenge. Members see this too, so it follows the web
// convention rather than a desktop one: on a phone it opens the native share
// sheet (Messages / WhatsApp / email), and everywhere else it copies the link.
// Clipboard is unavailable on insecure origins and in some in-app browsers, so
// there's a hidden-textarea fallback — and if even that fails it says so rather
// than claiming a copy that didn't happen.
function SeriesShareButton({ url, title, label, copiedLabel }) {
    const [state, setState] = useState('idle'); // idle | copied | failed
    const [hover, setHover] = useState(false);
    const timerRef = useRef(null);
    useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

    const flash = (next) => {
        setState(next);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setState('idle'), 2200);
    };

    const share = async () => {
        if (!url) return;
        // Native share sheet first where the platform offers one. A user
        // cancelling it (AbortError) must be silent — it isn't a failure.
        if (typeof navigator !== 'undefined' && navigator.share) {
            try {
                await navigator.share({ title: title || 'Challenge', url });
                return;
            } catch (err) {
                if (err && (err.name === 'AbortError' || err.name === 'NotAllowedError')) return;
                /* anything else: fall through to copying */
            }
        }
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(url);
                flash('copied');
                return;
            }
        } catch (_) { /* fall through to the legacy path */ }
        try {
            const ta = document.createElement('textarea');
            ta.value = url;
            ta.setAttribute('readonly', '');
            ta.style.position = 'fixed';
            ta.style.opacity = '0';
            document.body.appendChild(ta);
            ta.select();
            const ok = document.execCommand('copy');
            document.body.removeChild(ta);
            flash(ok ? 'copied' : 'failed');
        } catch (_) { flash('failed'); }
    };

    const copied = state === 'copied';
    return (
        <button
            onClick={share}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            title={url}
            style={{
                display: 'inline-flex', alignItems: 'center', gap: 7, flexShrink: 0,
                fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, fontWeight: 600,
                color: copied ? 'var(--mom-primary)' : 'var(--mom-charcoal)',
                background: hover && !copied ? 'var(--mom-cream)' : 'transparent',
                border: `1px solid ${copied ? 'color-mix(in srgb, var(--mom-primary) 45%, transparent)' : 'var(--mom-cream-dark)'}`,
                borderRadius: 12, padding: '8px 14px', cursor: 'pointer',
                transition: 'background 0.15s ease, border-color 0.15s ease, color 0.15s ease',
            }}
        >
            {copied ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" /><polyline points="16 6 12 2 8 6" /><line x1="12" y1="2" x2="12" y2="15" /></svg>
            )}
            {state === 'failed' ? 'Copy failed' : copied ? (copiedLabel || 'Link copied') : (label || 'Share')}
        </button>
    );
}

// Restrained chip row: days / total length / admin free-text pills. Outlined and
// muted — filled color blocks read cheap on this surface.
function SeriesMetaPills({ pills }) {
    const list = (pills || []).filter((p) => p && String(p).trim());
    if (list.length === 0) return null;
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {list.map((p, i) => (
                <span key={i} style={{
                    fontFamily: 'var(--mom-font-sans)', fontSize: 11.5, fontWeight: 600,
                    letterSpacing: '0.02em',
                    color: 'color-mix(in srgb, var(--mom-charcoal) 72%, var(--mom-cream))',
                    background: 'transparent',
                    border: '1px solid color-mix(in srgb, var(--mom-primary) 22%, var(--mom-cream-dark))',
                    borderRadius: 999, padding: '5px 12px', whiteSpace: 'nowrap',
                }}>{p}</span>
            ))}
        </div>
    );
}

// Benefit list. Accent check glyphs (SVG, never emoji).
function SeriesHighlights({ heading, items }) {
    const list = (items || []).filter((h) => h && String(h).trim());
    if (list.length === 0) return null;
    return (
        <div style={SERIES_CARD_STYLE}>
            {heading ? <h3 style={SERIES_HEADING_STYLE}>{heading}</h3> : null}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {list.map((h, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--mom-primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 3 }}><polyline points="20 6 9 17 4 12" /></svg>
                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14.5, color: 'var(--mom-charcoal)', lineHeight: 1.55 }}>{h}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

// The flexible core: arbitrary {heading, body} blocks, so one challenge can say
// "What You'll Need" and another "How It Works" with no code change.
function SeriesSections({ sections }) {
    const list = (sections || []).filter((s) => s && ((s.heading && s.heading.trim()) || (s.body && s.body.trim())));
    if (list.length === 0) return null;
    return (
        <>
            {list.map((s, i) => (
                <div key={i} style={SERIES_CARD_STYLE}>
                    {s.heading && s.heading.trim() ? <h3 style={SERIES_HEADING_STYLE}>{s.heading}</h3> : null}
                    {s.body && s.body.trim() ? (
                        <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14.5, color: 'var(--mom-warm-gray)', lineHeight: 1.65 }}>
                            <FormattedText text={s.body} />
                        </div>
                    ) : null}
                </div>
            ))}
        </>
    );
}

// FAQ accordion — one open row at a time. Owns its open index so expanding a
// question never re-renders the rest of the Workouts surface.
function SeriesFaq({ heading, faqs }) {
    const [openIdx, setOpenIdx] = useState(null);
    const list = (faqs || []).filter((f) => f && f.question && f.question.trim());
    if (list.length === 0) return null;
    return (
        <div style={SERIES_CARD_STYLE}>
            {heading ? <h3 style={SERIES_HEADING_STYLE}>{heading}</h3> : null}
            <div>
                {list.map((f, i) => (
                    <div key={i} style={{ borderBottom: i < list.length - 1 ? '1px solid var(--mom-cream-dark)' : 'none' }}>
                        <button
                            onClick={() => setOpenIdx(openIdx === i ? null : i)}
                            aria-expanded={openIdx === i}
                            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 0', minHeight: 44, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                        >
                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14.5, fontWeight: 600, color: 'var(--mom-charcoal)', flex: 1 }}>{f.question}</span>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--mom-warm-gray)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transition: 'transform 0.2s ease', transform: openIdx === i ? 'rotate(180deg)' : 'rotate(0deg)' }}><polyline points="6 9 12 15 18 9" /></svg>
                        </button>
                        <div style={{ maxHeight: openIdx === i ? 1000 : 0, overflow: 'hidden', transition: 'max-height 0.25s ease' }}>
                            <div style={{ paddingBottom: 14, fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-warm-gray)', lineHeight: 1.65 }}>
                                <FormattedText text={f.answer} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// The three info blocks in one unit, so the detail view can place them either
// above or below the day list (per-series `detailsFirst`) without duplicating
// markup. Renders nothing at all when the series carries none of the fields.
function SeriesInfoBlocks({ series, siteConfig }) {
    if (!series) return null;
    const highlightsHeading = (series.highlightsHeading && series.highlightsHeading.trim())
        || siteConfig.seriesHighlightsHeading || "What's Included";
    const faqHeading = (series.faqsHeading && series.faqsHeading.trim())
        || siteConfig.seriesFaqHeading || 'Questions';
    return (
        <>
            <SeriesHighlights heading={highlightsHeading} items={series.highlights} />
            <SeriesSections sections={series.sections} />
            <SeriesFaq heading={faqHeading} faqs={series.faqs} />
        </>
    );
}

export { SeriesShareButton, SeriesMetaPills, SeriesHighlights, SeriesSections, SeriesFaq, SeriesInfoBlocks, SERIES_CARD_STYLE, SERIES_HEADING_STYLE };
